<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('adminPayroll.title')" :description="t('adminPayroll.description')">
      <template #actions>
        <AppButton size="sm" :disabled="busy" @click="openGenerate()">
          <Plus class="h-4 w-4" /> {{ t('payrollPage.generateMonth', { month: monthLabel(view.year, view.month) }) }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Month navigation + year overview -->
    <AppCard class="p-4 space-y-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <AppButton variant="outline" size="icon" :title="t('payrollPage.prevMonth')" @click="shiftMonth(-1)">
            <ChevronLeft class="h-4 w-4 rtl:rotate-180" />
          </AppButton>
          <div class="min-w-[11rem] text-center">
            <p class="text-lg font-semibold text-foreground leading-tight">{{ monthLabel(view.year, view.month) }}</p>
            <p class="text-xs text-muted-foreground">
              <AppBadge :variant="stateVariant(currentMonthState)" class="mt-0.5">{{ stateLabel(currentMonthState) }}</AppBadge>
            </p>
          </div>
          <AppButton variant="outline" size="icon" :title="t('payrollPage.nextMonth')" @click="shiftMonth(1)">
            <ChevronRight class="h-4 w-4 rtl:rotate-180" />
          </AppButton>
          <AppButton v-if="!isCurrentMonth" variant="ghost" size="sm" @click="goToMonth(today.year, today.month)">
            {{ t('payrollPage.thisMonth') }}
          </AppButton>
        </div>

        <div class="flex items-center gap-1 text-sm">
          <AppButton variant="ghost" size="icon-sm" :title="t('payrollPage.prevYear')" @click="goToMonth(view.year - 1, view.month)">
            <ChevronLeft class="h-4 w-4 rtl:rotate-180" />
          </AppButton>
          <span class="font-medium tabular-nums w-12 text-center">{{ view.year }}</span>
          <AppButton variant="ghost" size="icon-sm" :title="t('payrollPage.nextYear')" @click="goToMonth(view.year + 1, view.month)">
            <ChevronRight class="h-4 w-4 rtl:rotate-180" />
          </AppButton>
        </div>
      </div>

      <div class="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
        <button
          v-for="m in overview"
          :key="m.month"
          type="button"
          :class="[
            'rounded-md border px-2 py-2 text-start transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
            m.month === view.month ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:bg-muted/40',
          ]"
          :title="`${monthLabel(view.year, m.month)}: ${stateLabel(m.state)}${m.total ? ' · ' + t('payrollPage.recordsCount', { n: m.total }) : ''}`"
          @click="goToMonth(view.year, m.month)"
        >
          <span class="block text-xs font-medium text-foreground">{{ monthName(m.month, 'short') }}</span>
          <span class="mt-1 flex items-center gap-1.5">
            <span :class="['h-2 w-2 rounded-full shrink-0', stateDot(m.state)]" />
            <span class="text-xs text-muted-foreground tabular-nums">{{ m.total || '–' }}</span>
          </span>
        </button>
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span v-for="s in ['none', 'draft', 'finalized', 'paid']" :key="s" class="flex items-center gap-1.5">
          <span :class="['h-2 w-2 rounded-full', stateDot(s)]" /> {{ stateLabel(s) }}
        </span>
      </div>
    </AppCard>

    <!-- Result of the last generate / bulk action -->
    <AppCard v-if="lastResult" class="p-4 border-primary/30 bg-primary/5">
      <div class="flex items-start justify-between gap-3">
        <div class="space-y-1 text-sm">
          <p class="font-semibold text-foreground">{{ t('payrollPage.resultTitle', { month: monthLabel(lastResult.year, lastResult.month) }) }}</p>
          <p class="text-foreground">
            <template v-if="resultParts.length">{{ resultParts.join(' · ') }}</template>
            <template v-else>{{ t('payrollPage.resultNothing') }}</template>
          </p>
          <p v-if="lastResult.skipped.length" class="text-xs text-muted-foreground">
            {{ lastResult.skipped.map(s => s.employee_name).join(', ') }}
          </p>
          <ul v-if="lastResult.errors.length" class="text-xs text-destructive list-disc ps-4">
            <li v-for="e in lastResult.errors" :key="e.employee_id">{{ e.employee_name }}: {{ e.error }}</li>
          </ul>
        </div>
        <AppButton variant="ghost" size="icon-sm" :title="t('payrollPage.dismiss')" @click="lastResult = null">
          <X class="h-4 w-4" />
        </AppButton>
      </div>
    </AppCard>

    <!-- Summary cards -->
    <div v-if="summary && summary.total_records > 0" class="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <AppStatCard :label="t('adminPayroll.totalEmployees')" :value="summary.total_employees || 0" :icon="UsersIcon" iconBg="bg-blue-50" iconColor="text-blue-600" />
      <AppStatCard :label="t('adminPayroll.totalPayroll')" :value="money(summary.total_final_salary)" :sub="t('adminPayroll.afterDeductions')" :icon="DollarSign" iconBg="bg-emerald-50" iconColor="text-emerald-600" />
      <AppStatCard :label="t('adminPayroll.totalDeductions')" :value="money(summary.total_deductions)" :icon="TrendingDown" iconBg="bg-red-50" iconColor="text-red-600" />
      <AppStatCard :label="t('adminPayroll.avgSalary')" :value="money(summary.avg_final_salary)" :icon="BarChart2" iconBg="bg-purple-50" iconColor="text-purple-600" />
      <AppStatCard :label="t('adminPayroll.totalOvertime')" :value="money(summary.total_overtime_pay)" :sub="`${Number(summary.total_overtime_hours || 0).toFixed(1)}h`" :icon="Clock" iconBg="bg-amber-50" iconColor="text-amber-600" />
    </div>

    <!-- Employees with no payroll this month -->
    <AppCard v-if="missingEmployees.length && monthCounts.total > 0" class="p-4 border-amber-200 bg-amber-50/60">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="text-sm">
          <p class="font-medium text-amber-800">{{ t('payrollPage.missingTitle', { n: missingEmployees.length, month: monthLabel(view.year, view.month) }) }}</p>
          <p class="text-xs text-amber-700 mt-0.5">{{ missingEmployees.map(e => e.name).join(', ') }}</p>
        </div>
        <AppButton size="sm" variant="outline" :loading="busyAction === 'missing'" :disabled="busy" @click="generateMissing">
          {{ t('payrollPage.generateMissing') }}
        </AppButton>
      </div>
    </AppCard>

    <!-- Toolbar -->
    <div class="flex flex-col lg:flex-row lg:items-center gap-3">
      <div class="flex flex-1 gap-3">
        <div class="relative flex-1 max-w-sm">
          <Search class="absolute start-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <AppInput v-model="search" :placeholder="t('payrollPage.searchPlaceholder')" class="ps-8" />
        </div>
        <AppSelect v-model="status" class="w-40">
          <option value="">{{ t('payrollPage.allStatuses') }}</option>
          <option value="draft">{{ t('adminPayroll.draft') }}</option>
          <option value="finalized">{{ t('adminPayroll.finalized') }}</option>
          <option value="paid">{{ t('adminPayroll.paid') }}</option>
        </AppSelect>
      </div>
      <div class="flex flex-wrap gap-2">
        <AppButton v-if="monthCounts.draft" variant="outline" size="sm" :loading="busyAction === 'regenerate'" :disabled="busy" @click="regenerateDrafts">
          <RefreshCw class="h-4 w-4" /> {{ t('payrollPage.regenerateDrafts') }}
        </AppButton>
        <AppButton v-if="monthCounts.draft" size="sm" :loading="busyAction === 'finalize'" :disabled="busy" @click="bulkFinalize">
          <CheckCircle class="h-4 w-4" /> {{ t('payrollPage.finalizeAll', { n: monthCounts.draft }) }}
        </AppButton>
        <AppButton v-if="monthCounts.finalized" variant="success" size="sm" :loading="busyAction === 'paid'" :disabled="busy" @click="bulkMarkPaid">
          <BadgeCheck class="h-4 w-4" /> {{ t('payrollPage.markAllPaid', { n: monthCounts.finalized }) }}
        </AppButton>
      </div>
    </div>

    <!-- Table -->
    <AppCard>
      <div v-if="loading && !records.length" class="flex items-center justify-center py-16 gap-3 text-muted-foreground">
        <AppSpinner />
        <span class="text-sm">{{ t('payrollPage.loading') }}</span>
      </div>

      <AppEmptyState
        v-else-if="!records.length && (search || status)"
        :icon="FileText"
        :title="t('adminPayroll.noPayroll')"
        :description="t('payrollPage.emptyFiltered')"
      >
        <template #action>
          <AppButton size="sm" variant="outline" @click="clearFilters">{{ t('payrollPage.clearFilters') }}</AppButton>
        </template>
      </AppEmptyState>

      <AppEmptyState
        v-else-if="!records.length"
        :icon="FileText"
        :title="t('payrollPage.emptyTitle', { month: monthLabel(view.year, view.month) })"
        :description="t('payrollPage.emptyDescription')"
      >
        <template #action>
          <AppButton size="sm" @click="openGenerate()">
            <Plus class="h-4 w-4" /> {{ t('payrollPage.generateMonth', { month: monthLabel(view.year, view.month) }) }}
          </AppButton>
        </template>
      </AppEmptyState>

      <div v-else :class="['overflow-x-auto transition-opacity', loading ? 'opacity-60' : '']">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-muted/50 border-b border-border">
              <th class="px-4 py-3 text-start font-medium text-muted-foreground">{{ t('adminPayroll.employee') }}</th>
              <th class="px-4 py-3 text-end font-medium text-muted-foreground">{{ t('adminPayroll.basicSalary') }}</th>
              <th class="px-4 py-3 text-end font-medium text-muted-foreground">{{ t('adminPayroll.totalDeductions') }}</th>
              <th class="px-4 py-3 text-end font-medium text-muted-foreground">{{ t('adminPayroll.overtimePay') }}</th>
              <th class="px-4 py-3 text-end font-medium text-muted-foreground">{{ t('payrollPage.adjustment') }}</th>
              <th class="px-4 py-3 text-end font-medium text-muted-foreground">{{ t('adminPayroll.netPay') }}</th>
              <th class="px-4 py-3 text-start font-medium text-muted-foreground">{{ t('common.status') }}</th>
              <th class="px-4 py-3 text-end font-medium text-muted-foreground">{{ t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            <tr
              v-for="record in records"
              :key="record.id"
              :class="['transition-colors', highlighted.has(record.id) ? 'bg-emerald-50/70' : 'hover:bg-muted/20']"
            >
              <td class="px-4 py-3">
                <button type="button" class="font-medium text-foreground hover:text-primary text-start" @click="viewDetails(record)">{{ record.employee_name }}</button>
                <p v-if="record.employee_code" class="text-xs text-muted-foreground">{{ record.employee_code }}</p>
              </td>
              <td class="px-4 py-3 font-mono text-end">{{ money(record.base_salary) }}</td>
              <td class="px-4 py-3 font-mono text-end text-destructive">{{ negMoney(record.total_deductions) }}</td>
              <td class="px-4 py-3 font-mono text-end text-emerald-600">{{ posMoney(record.overtime_pay) }}</td>
              <td class="px-4 py-3 font-mono text-end" :class="adjustmentClass(record.manual_adjustment)">
                {{ Number(record.manual_adjustment) ? signedMoney(record.manual_adjustment) : '' }}
              </td>
              <td class="px-4 py-3 font-mono text-end font-semibold text-foreground">{{ money(record.final_salary) }}</td>
              <td class="px-4 py-3">
                <AppBadge :variant="stateVariant(record.status)">{{ stateLabel(record.status) }}</AppBadge>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center justify-end gap-1">
                  <AppButton variant="ghost" size="icon-sm" :title="t('payrollPage.actionView')" @click="viewDetails(record)">
                    <Eye class="h-3.5 w-3.5" />
                  </AppButton>
                  <template v-if="record.status === 'draft'">
                    <AppButton variant="ghost" size="icon-sm" :title="t('payrollPage.actionRegenerate')" :disabled="busy" @click="regenerateOne(record)">
                      <RefreshCw :class="['h-3.5 w-3.5', busyRow === record.id ? 'animate-spin' : '']" />
                    </AppButton>
                    <AppButton variant="ghost" size="icon-sm" class="text-blue-600 hover:bg-blue-50" :title="t('payrollPage.actionFinalize')" :disabled="busy" @click="finalizeOne(record)">
                      <CheckCircle class="h-3.5 w-3.5" />
                    </AppButton>
                    <AppButton variant="ghost" size="icon-sm" class="text-destructive hover:bg-red-50" :title="t('payrollPage.actionDelete')" :disabled="busy" @click="deleteOne(record)">
                      <Trash2 class="h-3.5 w-3.5" />
                    </AppButton>
                  </template>
                  <AppButton v-if="record.status === 'finalized'" variant="ghost" size="icon-sm" class="text-emerald-600 hover:bg-emerald-50" :title="t('payrollPage.actionMarkPaid')" :disabled="busy" @click="markPaidOne(record)">
                    <BadgeCheck class="h-3.5 w-3.5" />
                  </AppButton>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="totals">
            <tr class="bg-muted/40 border-t-2 border-border font-semibold">
              <td class="px-4 py-3 text-foreground">{{ t('payrollPage.totals') }}</td>
              <td class="px-4 py-3 font-mono text-end">{{ money(totals.base_salary) }}</td>
              <td class="px-4 py-3 font-mono text-end text-destructive">{{ negMoney(totals.total_deductions) }}</td>
              <td class="px-4 py-3 font-mono text-end text-emerald-600">{{ posMoney(totals.overtime_pay) }}</td>
              <td class="px-4 py-3 font-mono text-end" :class="adjustmentClass(totals.manual_adjustment)">
                {{ Number(totals.manual_adjustment) ? signedMoney(totals.manual_adjustment) : '' }}
              </td>
              <td class="px-4 py-3 font-mono text-end text-foreground">{{ money(totals.final_salary) }}</td>
              <td colspan="2" />
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
        <p class="text-xs text-muted-foreground">{{ t('payrollPage.pageOf', { page: pagination.page, pages: pagination.totalPages, total: pagination.total }) }}</p>
        <div class="flex gap-2">
          <AppButton variant="outline" size="sm" :disabled="pagination.page <= 1 || loading" @click="changePage(pagination.page - 1)">{{ t('payrollPage.prev') }}</AppButton>
          <AppButton variant="outline" size="sm" :disabled="pagination.page >= pagination.totalPages || loading" @click="changePage(pagination.page + 1)">{{ t('payrollPage.next') }}</AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Generate modal -->
    <AppModal v-model="showGenerate" :title="t('payrollPage.generateTitle')" size="sm">
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('adminPayroll.month') }}</label>
            <AppSelect v-model="generateForm.month">
              <option v-for="m in 12" :key="m" :value="m">{{ monthName(m, 'long') }}</option>
            </AppSelect>
          </div>
          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('adminPayroll.year') }}</label>
            <AppSelect v-model="generateForm.year">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </AppSelect>
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">
            {{ t('adminPayroll.employee') }} <span class="font-normal">({{ t('adminPayroll.optionalAll') }})</span>
          </label>
          <AppSelect v-model="generateForm.employee_id" :disabled="loadingEmployees">
            <option value="">{{ t('adminPayroll.allEmployees') }}</option>
            <option v-for="emp in employees" :key="emp.id" :value="emp.id">
              {{ employeeName(emp) }}{{ emp.employee_code ? ' (' + emp.employee_code + ')' : '' }}
            </option>
          </AppSelect>
        </div>
        <p class="text-xs text-muted-foreground">{{ t('payrollPage.generateHint') }}</p>
      </div>
      <template #footer>
        <AppButton variant="outline" :disabled="busyAction === 'generate'" @click="showGenerate = false">{{ t('common.cancel') }}</AppButton>
        <AppButton :loading="busyAction === 'generate'" @click="submitGenerate">{{ t('common.generate') }}</AppButton>
      </template>
    </AppModal>

    <!-- Confirm dialog -->
    <AppModal v-model="confirmState.open" :title="confirmState.title" size="sm" @update:modelValue="v => !v && answerConfirm(false)">
      <p class="text-sm text-foreground">{{ confirmState.message }}</p>
      <template #footer>
        <AppButton variant="outline" @click="answerConfirm(false)">{{ t('common.cancel') }}</AppButton>
        <AppButton :variant="confirmState.variant" @click="answerConfirm(true)">{{ confirmState.confirmLabel || t('payrollPage.confirm') }}</AppButton>
      </template>
    </AppModal>

    <!-- Details modal -->
    <AppModal v-model="showDetails" size="xl">
      <template #header>
        <div v-if="selected">
          <h2 class="text-base font-semibold text-foreground">{{ selected.employee?.name }}</h2>
          <p class="text-xs text-muted-foreground mt-0.5">
            {{ monthLabel(selected.period?.year, selected.period?.month) }} · {{ t('payrollPage.detailsTitle') }}
            <AppBadge :variant="stateVariant(selected.status)" class="ms-1">{{ stateLabel(selected.status) }}</AppBadge>
          </p>
        </div>
      </template>

      <div v-if="selected" class="space-y-5">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="rounded-lg bg-blue-50 p-3">
            <p class="text-xs text-muted-foreground">{{ t('payrollPage.baseSalary') }}</p>
            <p class="text-lg font-bold text-blue-700 font-mono">{{ money(selected.earnings?.baseSalary) }}</p>
          </div>
          <div class="rounded-lg bg-amber-50 p-3">
            <p class="text-xs text-muted-foreground">{{ t('payrollPage.overtimePay') }}</p>
            <p class="text-lg font-bold text-amber-700 font-mono">{{ posMoney(selected.earnings?.overtimePay) }}</p>
            <p class="text-xs text-muted-foreground">{{ selected.earnings?.overtimeHours || 0 }}h</p>
          </div>
          <div class="rounded-lg bg-red-50 p-3">
            <p class="text-xs text-muted-foreground">{{ t('payrollPage.totalDeductions') }}</p>
            <p class="text-lg font-bold text-red-700 font-mono">{{ negMoney(selected.deductions?.totalDeductions) }}</p>
          </div>
          <div class="rounded-lg bg-emerald-50 p-3">
            <p class="text-xs text-muted-foreground">{{ t('payrollPage.netPay') }}</p>
            <p class="text-lg font-bold text-emerald-700 font-mono">{{ money(selected.summary?.netPay) }}</p>
            <p v-if="Number(selected.earnings?.manualAdjustment)" class="text-xs" :class="adjustmentClass(selected.earnings.manualAdjustment)">
              {{ t('payrollPage.adjustment') }} {{ signedMoney(selected.earnings.manualAdjustment) }}
            </p>
          </div>
        </div>

        <div class="space-y-4">
          <details v-if="selected.earnings?.overtimeDetails?.length" open>
            <summary class="text-sm font-semibold text-foreground cursor-pointer py-1.5">
              {{ t('payrollPage.overtimeSessions', { n: selected.earnings.overtimeDetails.length, h: selected.earnings.overtimeHours }) }}
            </summary>
            <div class="mt-2 rounded-lg border border-border overflow-hidden">
              <div v-for="ot in selected.earnings.overtimeDetails" :key="ot.sessionId" class="flex justify-between gap-3 px-4 py-2 border-b border-border last:border-0 text-sm">
                <span class="text-foreground">{{ formatDate(ot.date) }} · {{ ot.hours }}h @ {{ ot.multiplier }}x <span v-if="ot.approvedBy" class="text-xs text-muted-foreground">({{ ot.approvedBy }})</span></span>
                <span class="font-mono font-medium text-emerald-600">{{ posMoney(ot.amount) }}</span>
              </div>
              <div class="flex justify-between px-4 py-2 bg-muted/40 text-sm font-semibold">
                <span>{{ t('payrollPage.total') }}</span>
                <span class="font-mono text-emerald-600">{{ posMoney(selected.earnings.overtimePay) }}</span>
              </div>
            </div>
          </details>

          <template v-for="section in deductionSections(selected)" :key="section.key">
            <details v-if="section.items?.length" open>
              <summary class="text-sm font-semibold text-foreground cursor-pointer py-1.5">{{ section.label }} ({{ section.items.length }})</summary>
              <div class="mt-2 rounded-lg border border-border overflow-hidden">
                <div v-for="(d, i) in section.items" :key="d.id || i" class="flex justify-between gap-3 px-4 py-2 border-b border-border last:border-0 text-sm">
                  <span class="text-foreground">{{ formatDate(d.date) }} · {{ d.description }}</span>
                  <span class="font-mono font-medium text-destructive">{{ negMoney(d.amount) }}</span>
                </div>
                <div class="flex justify-between px-4 py-2 bg-muted/40 text-sm font-semibold">
                  <span>{{ t('payrollPage.total') }}</span>
                  <span class="font-mono text-destructive">{{ negMoney(section.total) }}</span>
                </div>
              </div>
            </details>
          </template>
        </div>

        <div v-if="selected.leaveBalances?.length">
          <p class="text-sm font-semibold text-foreground mb-2">{{ t('payrollPage.leaveBalances', { year: selected.period?.year }) }}</p>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div v-for="lb in selected.leaveBalances" :key="lb.bankCode" class="rounded-lg bg-primary/5 p-3">
              <p class="text-xs font-medium text-primary">{{ lb.bankName }}</p>
              <p class="text-sm text-foreground mt-1">
                <span class="font-semibold">{{ lb.remaining }}</span> / {{ lb.total ?? lb.allocated }} {{ t('payrollPage.days') }}
              </p>
              <p v-if="lb.carriedOver > 0" class="text-xs text-muted-foreground">
                {{ t('payrollPage.allocatedPlusCarry', { a: lb.allocated, c: lb.carriedOver }) }}
              </p>
              <div class="mt-1.5 w-full bg-primary/20 rounded-full h-1.5 overflow-hidden">
                <div class="bg-primary h-1.5 rounded-full" :style="{ width: Math.min(100, lb.percentage) + '%' }" />
              </div>
            </div>
          </div>
        </div>

        <div class="pt-3 border-t border-border text-xs text-muted-foreground space-y-0.5">
          <p><span class="font-medium text-foreground">{{ t('payrollPage.generatedBy') }}:</span> {{ selected.generatedBy }} {{ t('payrollPage.on') }} {{ formatDateTime(selected.generatedAt) }}</p>
          <p v-if="selected.finalizedBy"><span class="font-medium text-foreground">{{ t('payrollPage.finalizedBy') }}:</span> {{ selected.finalizedBy }} {{ t('payrollPage.on') }} {{ formatDateTime(selected.finalizedAt) }}</p>
          <p v-if="selected.paymentInfo?.paidBy"><span class="font-medium text-foreground">{{ t('payrollPage.paidBy') }}:</span> {{ selected.paymentInfo.paidBy }} {{ t('payrollPage.on') }} {{ formatDateTime(selected.paymentInfo.paidAt) }}</p>
        </div>

        <details v-if="selected.auditLog?.length">
          <summary class="text-sm font-semibold text-foreground cursor-pointer py-1.5">{{ t('payrollPage.auditTrail', { n: selected.auditLog.length }) }}</summary>
          <div class="mt-2 rounded-lg border border-border overflow-x-auto">
            <table class="w-full text-xs">
              <thead><tr class="bg-muted/50 border-b border-border">
                <th class="px-3 py-2 text-start font-medium text-muted-foreground">{{ t('payrollPage.auditAction') }}</th>
                <th class="px-3 py-2 text-start font-medium text-muted-foreground">{{ t('payrollPage.auditBy') }}</th>
                <th class="px-3 py-2 text-start font-medium text-muted-foreground">{{ t('payrollPage.auditTime') }}</th>
                <th class="px-3 py-2 text-start font-medium text-muted-foreground">{{ t('payrollPage.auditSalary') }}</th>
              </tr></thead>
              <tbody class="divide-y divide-border">
                <tr v-for="(entry, idx) in selected.auditLog" :key="idx">
                  <td class="px-3 py-2 font-medium">{{ auditLabel(entry.action) }}</td>
                  <td class="px-3 py-2 text-muted-foreground">{{ entry.userName || '' }}</td>
                  <td class="px-3 py-2 text-muted-foreground">{{ formatDateTime(entry.timestamp) }}</td>
                  <td class="px-3 py-2 font-mono">
                    <span v-if="entry.oldFinalSalary !== entry.newFinalSalary && entry.oldFinalSalary != null" dir="ltr">{{ money(entry.oldFinalSalary) }} → {{ money(entry.newFinalSalary) }}</span>
                    <span v-else-if="entry.oldFinalSalary == null && entry.newFinalSalary != null">{{ money(entry.newFinalSalary) }}</span>
                    <span v-else class="text-muted-foreground">{{ t('payrollPage.noChange') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>

        <!-- Manual adjustment -->
        <div v-if="selected.status !== 'paid'" class="pt-3 border-t border-border">
          <div class="flex items-center justify-between mb-3">
            <p class="text-sm font-semibold text-foreground">{{ t('payrollPage.manualAdjustment') }}</p>
            <AppButton v-if="!showAdjustment" size="sm" variant="outline" @click="startAdjustment">
              <PencilLine class="h-3.5 w-3.5" /> {{ t('payrollPage.edit') }}
            </AppButton>
          </div>
          <div v-if="Number(selected.earnings?.manualAdjustment) && !showAdjustment" class="rounded-lg bg-primary/5 p-3 text-sm">
            <p class="text-foreground"><span class="font-medium">{{ t('payrollPage.amount') }}:</span>
              <span :class="adjustmentClass(selected.earnings.manualAdjustment)"> {{ signedMoney(selected.earnings.manualAdjustment) }}</span>
            </p>
            <p class="text-foreground mt-1"><span class="font-medium">{{ t('payrollPage.reason') }}:</span> {{ selected.earnings.manualAdjustmentReason }}</p>
          </div>
          <div v-if="showAdjustment" class="rounded-lg bg-muted/50 p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('payrollPage.amountLabel', { c: currencySymbol }) }}</label>
              <AppInput v-model="adjustmentForm.amount" type="number" step="0.01" :placeholder="t('payrollPage.amountPlaceholder')" />
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('payrollPage.reason') }}</label>
              <AppInput v-model="adjustmentForm.reason" :placeholder="t('payrollPage.reasonPlaceholder')" />
            </div>
            <div class="flex gap-2">
              <AppButton size="sm" :loading="savingAdjustment" @click="saveAdjustment">{{ t('payrollPage.save') }}</AppButton>
              <AppButton size="sm" variant="outline" :disabled="savingAdjustment" @click="showAdjustment = false">{{ t('payrollPage.cancel') }}</AppButton>
            </div>
          </div>
        </div>
      </div>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  Plus, Eye, CheckCircle, BadgeCheck, Trash2, FileText, RefreshCw, Search, X,
  ChevronLeft, ChevronRight, Users as UsersIcon, DollarSign, TrendingDown, BarChart2, Clock, PencilLine,
} from 'lucide-vue-next'
import {
  AppPageHeader, AppCard, AppStatCard, AppBadge, AppButton,
  AppInput, AppSelect, AppModal, AppEmptyState, AppSpinner,
} from '@/components/ui'
import payrollService from '@/services/payrollService'
import { adminService } from '@/services/api'
import { useCurrency } from '@/composables/useCurrency'
import { toast } from '@/composables/useToast'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { getCurrencySymbol, loadCurrency } = useCurrency()
const currencySymbol = computed(() => getCurrencySymbol())

const now = new Date()
const today = { year: now.getFullYear(), month: now.getMonth() + 1 }

// ── View state (mirrored in the URL: ?month=2026-10&status=draft&q=ahmad) ──
const view = reactive(parseMonth(route.query.month) || { ...today })
const status = ref(typeof route.query.status === 'string' ? route.query.status : '')
const search = ref(typeof route.query.q === 'string' ? route.query.q : '')
const page = ref(1)

const loading = ref(false)
const records = ref([])
const totals = ref(null)
const summary = ref(null)
const missingEmployees = ref([])
const overview = ref(emptyOverview())
const pagination = reactive({ page: 1, totalPages: 1, total: 0 })

const busyAction = ref('')   // 'generate' | 'regenerate' | 'finalize' | 'paid' | 'missing'
const busyRow = ref(null)
const busy = computed(() => !!busyAction.value || busyRow.value !== null)
const lastResult = ref(null)
const highlighted = ref(new Set())

const employees = ref([])
const loadingEmployees = ref(false)
const showGenerate = ref(false)
const generateForm = reactive({ year: today.year, month: today.month, employee_id: '' })
const yearOptions = computed(() => Array.from({ length: 8 }, (_, i) => today.year - 5 + i))

const showDetails = ref(false)
const selected = ref(null)
const showAdjustment = ref(false)
const adjustmentForm = reactive({ amount: '', reason: '' })
const savingAdjustment = ref(false)

const confirmState = reactive({ open: false, title: '', message: '', variant: 'default', confirmLabel: '', resolve: null })

// ── Helpers ──
function parseMonth(value) {
  const m = typeof value === 'string' && value.match(/^(\d{4})-(\d{1,2})$/)
  if (!m) return null
  const year = Number(m[1]); const month = Number(m[2])
  return month >= 1 && month <= 12 ? { year, month } : null
}
function emptyOverview() {
  return Array.from({ length: 12 }, (_, i) => ({ month: i + 1, state: 'none', total: 0, draft: 0, finalized: 0, paid: 0 }))
}
const intlLocale = computed(() => (locale.value === 'ar' ? 'ar-u-nu-latn' : 'en-US'))
function monthName(month, style = 'long') {
  return new Intl.DateTimeFormat(intlLocale.value, { month: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2000, month - 1, 1)))
}
function monthLabel(year, month) {
  if (!year || !month) return ''
  return new Intl.DateTimeFormat(intlLocale.value, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 1)))
}
function formatDate(value) {
  if (!value) return ''
  return new Date(value).toLocaleDateString(intlLocale.value, { year: 'numeric', month: 'short', day: 'numeric' })
}
function formatDateTime(value) {
  if (!value) return ''
  return new Date(value).toLocaleString(intlLocale.value, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}
function fixed(value) { return (Number(value) || 0).toFixed(2) }
// Amounts are wrapped in a left-to-right isolate so the sign stays in front in Arabic
const ltr = text => `\u2066${text}\u2069`
function money(value) { return ltr(`${currencySymbol.value}${fixed(value)}`) }
function negMoney(value) { return ltr(`-${currencySymbol.value}${fixed(value)}`) }
function posMoney(value) { return ltr(`+${currencySymbol.value}${fixed(value)}`) }
function signedMoney(value) {
  const n = Number(value) || 0
  return ltr(`${n >= 0 ? '+' : '-'}${currencySymbol.value}${fixed(Math.abs(n))}`)
}
function adjustmentClass(value) { return Number(value) >= 0 ? 'text-emerald-600' : 'text-destructive' }
function employeeName(emp) { return emp.name || [emp.first_name, emp.last_name].filter(Boolean).join(' ') }

const STATE = {
  none:      { key: 'stateNone',      variant: 'muted',   dot: 'bg-muted-foreground/30' },
  draft:     { key: 'stateDraft',     variant: 'warning', dot: 'bg-amber-500' },
  finalized: { key: 'stateFinalized', variant: 'default', dot: 'bg-blue-500' },
  paid:      { key: 'statePaid',      variant: 'success', dot: 'bg-emerald-500' },
}
function stateLabel(s) { return t(`payrollPage.${(STATE[s] || STATE.none).key}`) }
function stateVariant(s) { return (STATE[s] || STATE.none).variant }
function stateDot(s) { return (STATE[s] || STATE.none).dot }
function auditLabel(action) {
  const key = `payrollPage.auditActions.${action}`
  const label = t(key)
  return label === key ? action : label
}

const monthCounts = computed(() => overview.value.find(m => m.month === view.month) || { total: 0, draft: 0, finalized: 0, paid: 0 })
const currentMonthState = computed(() => monthCounts.value.state || 'none')
const isCurrentMonth = computed(() => view.year === today.year && view.month === today.month)

const resultParts = computed(() => {
  const r = lastResult.value
  if (!r) return []
  return [
    r.created.length && t('payrollPage.resultCreated', { n: r.created.length }),
    r.updated.length && t('payrollPage.resultUpdated', { n: r.updated.length }),
    r.skipped.length && t('payrollPage.resultSkipped', { n: r.skipped.length }),
    r.errors.length && t('payrollPage.resultErrors', { n: r.errors.length }),
  ].filter(Boolean)
})

function deductionSections(record) {
  const d = record.deductions || {}
  return [
    { key: 'absent', label: t('payrollPage.absentDays'), items: d.absentDaysDetails, total: d.absentDays },
    { key: 'late', label: t('payrollPage.lateArrivals'), items: d.lateArrivalsDetails, total: d.lateArrivals },
    { key: 'short', label: t('payrollPage.shortLeaves'), items: d.shortLeaveDetails, total: d.shortLeave },
    { key: 'early', label: t('payrollPage.earlyDepartures'), items: d.earlyDepartureDetails, total: d.earlyDeparture },
  ]
}

function errorMessage(error) {
  return error?.response?.data?.message || error?.message || t('payrollPage.actionFailed')
}

function askConfirm({ title, message, variant = 'default', confirmLabel = '' }) {
  return new Promise(resolve => {
    Object.assign(confirmState, { open: true, title, message, variant, confirmLabel, resolve })
  })
}
function answerConfirm(answer) {
  const resolve = confirmState.resolve
  confirmState.resolve = null
  confirmState.open = false
  if (resolve) resolve(answer)
}

// ── Loading ──
let loadSeq = 0
let overviewYear = null

async function loadMonth({ withOverview = false } = {}) {
  const seq = ++loadSeq
  loading.value = true
  try {
    const params = { year: view.year, month: view.month, page: page.value, limit: 50 }
    if (status.value) params.status = status.value
    if (search.value.trim()) params.search = search.value.trim()

    const needOverview = withOverview || overviewYear !== view.year
    const [list, sum, ov] = await Promise.all([
      payrollService.getPayrollRecords(params),
      payrollService.getSummary({ year: view.year, month: view.month }),
      needOverview ? payrollService.getOverview(view.year) : Promise.resolve(null),
    ])
    if (seq !== loadSeq) return // a newer load superseded this one

    records.value = list.data.records || []
    totals.value = records.value.length ? list.data.totals : null
    missingEmployees.value = list.data.missing_employees || []
    const p = list.data.pagination || {}
    pagination.page = p.page || 1
    pagination.totalPages = p.totalPages || 1
    pagination.total = p.total || 0
    summary.value = sum.data
    if (ov) {
      overview.value = ov.data.months
      overviewYear = ov.data.year
    }
  } catch (error) {
    if (seq === loadSeq) toast.error(errorMessage(error) || t('payrollPage.loadFailed'))
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

function syncUrl() {
  const query = { month: `${view.year}-${String(view.month).padStart(2, '0')}` }
  if (status.value) query.status = status.value
  if (search.value.trim()) query.q = search.value.trim()
  router.replace({ query }).catch(() => {})
}

function goToMonth(year, month) {
  view.year = year
  view.month = month
}
function shiftMonth(delta) {
  const d = new Date(Date.UTC(view.year, view.month - 1 + delta, 1))
  goToMonth(d.getUTCFullYear(), d.getUTCMonth() + 1)
}
function changePage(p) {
  page.value = p
  loadMonth()
}
function clearFilters() {
  status.value = ''
  search.value = ''
}

watch(() => [view.year, view.month], () => {
  page.value = 1
  lastResult.value = null
  syncUrl()
  loadMonth()
})
watch(status, () => { page.value = 1; syncUrl(); loadMonth() })
let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { page.value = 1; syncUrl(); loadMonth() }, 300)
})

function flash(ids) {
  highlighted.value = new Set(ids)
  setTimeout(() => { highlighted.value = new Set() }, 4000)
}

// ── Generate ──
function openGenerate(employeeId = '') {
  generateForm.year = view.year
  generateForm.month = view.month
  generateForm.employee_id = employeeId
  showGenerate.value = true
}

async function runGenerate({ year, month, employeeId = null }) {
  const payload = { year, month }
  if (employeeId) payload.employee_id = employeeId
  const response = await payrollService.generatePayroll(payload)
  return response.data
}

function showResult(result) {
  lastResult.value = result
  flash([...result.created, ...result.updated].map(r => r.payroll_record_id))
}

async function afterGenerate(result) {
  const sameMonth = result.year === view.year && result.month === view.month
  if (!sameMonth) {
    // Switching month triggers the load through the watcher; show the result after it
    goToMonth(result.year, result.month)
    await new Promise(r => setTimeout(r, 0))
  }
  await loadMonth({ withOverview: true })
  showResult(result)
}

async function submitGenerate() {
  busyAction.value = 'generate'
  try {
    const result = await runGenerate({
      year: Number(generateForm.year),
      month: Number(generateForm.month),
      employeeId: generateForm.employee_id || null,
    })
    showGenerate.value = false
    await afterGenerate(result)
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyAction.value = ''
  }
}

async function regenerateDrafts() {
  const ok = await askConfirm({
    title: t('payrollPage.confirmRegenerateTitle'),
    message: t('payrollPage.confirmRegenerateAll', { month: monthLabel(view.year, view.month) }),
  })
  if (!ok) return
  busyAction.value = 'regenerate'
  try {
    await afterGenerate(await runGenerate({ year: view.year, month: view.month }))
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyAction.value = ''
  }
}

async function regenerateOne(record) {
  const ok = await askConfirm({
    title: t('payrollPage.confirmRegenerateTitle'),
    message: t('payrollPage.confirmRegenerateOne', { name: record.employee_name, month: monthLabel(record.year, record.month) }),
  })
  if (!ok) return
  busyRow.value = record.id
  try {
    await afterGenerate(await runGenerate({ year: record.year, month: record.month, employeeId: record.employee_id }))
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyRow.value = null
  }
}

async function generateMissing() {
  busyAction.value = 'missing'
  const combined = { year: view.year, month: view.month, created: [], updated: [], skipped: [], errors: [] }
  try {
    for (const emp of missingEmployees.value) {
      try {
        const r = await runGenerate({ year: view.year, month: view.month, employeeId: emp.id })
        for (const k of ['created', 'updated', 'skipped', 'errors']) combined[k].push(...r[k])
      } catch (error) {
        combined.errors.push({ employee_id: emp.id, employee_name: emp.name, error: errorMessage(error) })
      }
    }
    await afterGenerate(combined)
  } finally {
    busyAction.value = ''
  }
}

// ── Status changes ──
async function finalizeOne(record) {
  const ok = await askConfirm({
    title: t('payrollPage.confirmFinalizeTitle'),
    message: t('payrollPage.confirmFinalizeOne', { name: record.employee_name, month: monthLabel(record.year, record.month) }),
  })
  if (!ok) return
  busyRow.value = record.id
  try {
    await payrollService.finalizePayroll(record.id)
    toast.success(t('payrollPage.finalizedOk'))
    await loadMonth({ withOverview: true })
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyRow.value = null
  }
}

async function markPaidOne(record) {
  const ok = await askConfirm({
    title: t('payrollPage.confirmPaidTitle'),
    message: t('payrollPage.confirmPaidOne', { name: record.employee_name, month: monthLabel(record.year, record.month) }),
    variant: 'success',
  })
  if (!ok) return
  busyRow.value = record.id
  try {
    await payrollService.markAsPaid(record.id)
    toast.success(t('payrollPage.paidOk'))
    await loadMonth({ withOverview: true })
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyRow.value = null
  }
}

async function deleteOne(record) {
  const ok = await askConfirm({
    title: t('payrollPage.confirmDeleteTitle'),
    message: t('payrollPage.confirmDeleteOne', { name: record.employee_name, month: monthLabel(record.year, record.month) }),
    variant: 'destructive',
    confirmLabel: t('payrollPage.actionDelete'),
  })
  if (!ok) return
  busyRow.value = record.id
  try {
    await payrollService.deletePayroll(record.id)
    toast.success(t('payrollPage.deletedOk'))
    await loadMonth({ withOverview: true })
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyRow.value = null
  }
}

async function runBulk(kind) {
  const isFinalize = kind === 'finalize'
  const n = isFinalize ? monthCounts.value.draft : monthCounts.value.finalized
  const ok = await askConfirm({
    title: t(isFinalize ? 'payrollPage.confirmFinalizeTitle' : 'payrollPage.confirmPaidTitle'),
    message: t(isFinalize ? 'payrollPage.confirmFinalizeAll' : 'payrollPage.confirmPaidAll', { n, month: monthLabel(view.year, view.month) }),
    variant: isFinalize ? 'default' : 'success',
  })
  if (!ok) return
  busyAction.value = isFinalize ? 'finalize' : 'paid'
  try {
    const response = isFinalize
      ? await payrollService.bulkFinalize(view.year, view.month)
      : await payrollService.bulkMarkPaid(view.year, view.month)
    const { count, errors } = response.data
    toast.success(t(isFinalize ? 'payrollPage.finalizedAllOk' : 'payrollPage.paidAllOk', { n: count }))
    if (errors?.length) toast.error(t('payrollPage.bulkSomeFailed', { n: errors.length }))
    await loadMonth({ withOverview: true })
    flash(response.data.done.map(d => d.payroll_record_id))
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    busyAction.value = ''
  }
}
const bulkFinalize = () => runBulk('finalize')
const bulkMarkPaid = () => runBulk('paid')

// ── Details + adjustment ──
async function viewDetails(record) {
  try {
    const response = await payrollService.getPayrollDetails(record.id)
    selected.value = response.data
    showAdjustment.value = false
    showDetails.value = true
  } catch (error) {
    toast.error(t('payrollPage.detailsFailed'))
  }
}

function startAdjustment() {
  adjustmentForm.amount = selected.value.earnings?.manualAdjustment ?? ''
  adjustmentForm.reason = selected.value.earnings?.manualAdjustmentReason || ''
  showAdjustment.value = true
}

async function saveAdjustment() {
  const amount = adjustmentForm.amount
  const reason = String(adjustmentForm.reason || '').trim()
  if (amount === '' || amount === null || isNaN(Number(amount))) { toast.error(t('payrollPage.amountInvalid')); return }
  if (!reason) { toast.error(t('payrollPage.reasonRequired')); return }
  savingAdjustment.value = true
  try {
    await payrollService.updateManualAdjustment(selected.value.id, Number(amount), reason)
    toast.success(t('payrollPage.adjustmentSaved'))
    showAdjustment.value = false
    const response = await payrollService.getPayrollDetails(selected.value.id)
    selected.value = response.data
    await loadMonth()
  } catch (error) {
    toast.error(errorMessage(error))
  } finally {
    savingAdjustment.value = false
  }
}

async function loadEmployees() {
  loadingEmployees.value = true
  try {
    const response = await adminService.getAllEmployees({ limit: 500 })
    const all = response.data?.employees || response.employees || []
    employees.value = all.filter(e => e.status === 'active')
  } catch (error) {
    console.error('Failed to load employees:', error)
  } finally {
    loadingEmployees.value = false
  }
}

onMounted(() => {
  loadCurrency()
  loadEmployees()
  syncUrl()
  loadMonth()
})
</script>
